import { lazy } from 'react';
import { Navigate } from "react-router-dom";
import Loadable from "../layouts/loader/Loadable";
import OAuthPopup from "../oauth/OAuth2Popup";
import CommissionSlabDetails from "../components/zone/CommissionSlabs";
import CommissionRuleDetails from "../components/zone/CommissionSlabDetail";
import PackageDetails from '../components/package/PackageDetails';
import PartnerUserList from '../components/user/PartnerUserList';
import PartnerUserCreate from '../components/user/PartnerUserCreate';
import PartnerUserUpdate from '../components/user/PartnerUserUpdate';
import OrphanInvoices from '../components/report/OrphanInvoices';

/****Layouts*****/
const FullLayout = Loadable(lazy(() => import("../layouts/FullLayout")));
const BlankLayout = Loadable(lazy(() => import("../layouts/BlankLayout")));
/***** Pages ****/

// Notification Components
const NotificationDashboard = Loadable(lazy(() => import("../views/notifications/NotificationDashboard")));
const NotificationTemplates = Loadable(lazy(() => import("../views/notifications/NotificationTemplates")));
const NotificationRoutes = Loadable(lazy(() => import("../views/notifications/NotificationRoutes")));
const NotificationEmailConfig = Loadable(lazy(() => import("../views/notifications/NotificationEmailConfig")));
const NotificationSmsConfig = Loadable(lazy(() => import("../views/notifications/NotificationSmsConfig")));
const NotificationSend = Loadable(lazy(() => import("../views/notifications/NotificationSend")));

const Company = Loadable(lazy(() => import("../components/company/Company")));
const CompanyDetails = Loadable(lazy(() => import("../components/company/CompanyDetails")));
const CompanyList = Loadable(lazy(() => import("../components/company/CompanyList")));

const DistributorList = Loadable(lazy(() => import("../components/distributor/DistributorList")));
const DistributorDetails = Loadable(lazy(() => import("../components/distributor/DistributorDetails")));
const Distributor = Loadable(lazy(() => import("../components/distributor/Distributor")));

const ServerGroupList = Loadable(lazy(() => import("../components/serverGroup/ServerGroupList")));
const ServerGroupDetails = Loadable(lazy(() => import("../components/serverGroup/ServerGroupDetails")));
const ServerGroup = Loadable(lazy(() => import("../components/serverGroup/ServerGroup")));

const ZoneGroupList = Loadable(lazy(() => import("../components/zoneGroup/ZoneGroupList")));
const ZoneGroupDetails = Loadable(lazy(() => import("../components/zoneGroup/ZoneGroupDetails")));
const ZoneGroup = Loadable(lazy(() => import("../components/zoneGroup/ZoneGroup")));

const ZoneList = Loadable(lazy(() => import("../components/zone/ZoneList")));
const ZoneDetails = Loadable(lazy(() => import("../components/zone/ZoneDetails")));
const Zone = Loadable(lazy(() => import("../components/zone/Zone")));
const ZoneStatement = Loadable(lazy(() => import("../components/zone/ZoneStatement")));
const ZoneCommission = Loadable(lazy(() => import("../components/zone/ZoneCommission")));
const ZoneRenew = Loadable(lazy(() => import("../components/zone/ZoneRenew")));
const ZoneBalance = Loadable(lazy(() => import("../components/zone/ZoneBalance")));
const AllZoneBalance = Loadable(lazy(() => import("../components/zone/AllZoneBalance")));
const ZoneAdjustment = Loadable(lazy(() => import("../components/zone/ZoneAdjustment")));
const ZoneWalletRecharge = Loadable(lazy(() => import("../components/zone/ZoneWalletRecharge")));
const ZoneTransactions = Loadable(lazy(() => import("../components/zone/ZoneTransactionHistory")));


const CommissionRuleList = Loadable(lazy(() => import("../components/zone/CommissionRuleList")));

const LegalOfficeList = Loadable(lazy(() => import("../components/legalOffice/LegalOfficeList")));
const LegalOfficeDetails = Loadable(lazy(() => import("../components/legalOffice/LegalOfficeDetails")));
const LegalOffice = Loadable(lazy(() => import("../components/legalOffice/LegalOffice")));

const SupportOfficeList = Loadable(lazy(() => import("../components/supportOffice/SupportOfficeList")));
const SupportOfficeDetails = Loadable(lazy(() => import("../components/supportOffice/SupportOfficeDetails")));
const SupportOffice = Loadable(lazy(() => import("../components/supportOffice/SupportOffice")));

const ServerList = Loadable(lazy(() => import("../components/server/ServerList")));
const ServerDetails = Loadable(lazy(() => import("../components/server/ServerDetails")));
const Server = Loadable(lazy(() => import("../components/server/Server")));

const PackageList = Loadable(lazy(() => import("../components/package/PackageList")));

const PackageTypeList = Loadable(lazy(() => import("../components/packageType/PackageTypeList")));
const PackageTypeDetails = Loadable(lazy(() => import("../components/packageType/PackageTypeDetails")));
const PackageType = Loadable(lazy(() => import("../components/packageType/PackageType")));

const IpLogSearch = Loadable(lazy(() => import("../components/iplog/IpLogSearch")));
const IpLogPdf = Loadable(lazy(() => import("../components/iplog/pdf/IpLogPdf")));

const Login = Loadable(lazy(() => import('../views/auth/Login')));

const Analytical = Loadable(lazy(() => import('../components/dashboard/Analytical')));
const UploadList = Loadable(lazy(() => import('../components/upload/UploadList')));
const InvoiceSyncCountReport = Loadable(lazy(() => import('../components/report/InvoiceSyncCountReport')));

const CachedCompanyList = Loadable(lazy(() => import('../components/cache/CachedCompanyList')));
const CachedServerList = Loadable(lazy(() => import("../components/cache/CachedServerList")));
const CachedZoneList = Loadable(lazy(() => import("../components/cache/CachedZoneList")));
const CachedPackageList = Loadable(lazy(() => import('../components/cache/CachedPackageList')));

const Customer = Loadable(lazy(() => import('../components/lookup/Customer')));
const CustomerList = Loadable(lazy(() => import('../components/lookup/CustomerList')));



/***** Auth Pages ****/
const Error = Loadable(lazy(() => import("../views/auth/Error")));
const Unauthorized = Loadable(lazy(() => import("../views/auth/Unauthorized")));

/*****Routes******/

const ThemeRoutes = [
  {
    path: "/",
    element: <FullLayout />,
    errorElement: <Error/>,
    children: [
      { path: '*', element: <Navigate to="/error" /> },
      { path: '/', element: <Navigate to="/login" /> },
      { path: "/company",
        children: [
          { path: '', element: <Navigate to="/company/list" /> },
          { path: 'add', element: <Company /> },
          { path: 'edit/:id', element: <Company /> },
          { path: 'list', element: <CompanyList /> },
          { path: 'details/:id', element: <CompanyDetails /> },
          { path: '*', element: <Navigate to="/company/list" /> },
        ]
      },
      { path: "/cache",
        children: [
          { path: 'companies/list', element: <CachedCompanyList /> },
          { path: 'servers/list', element: <CachedServerList /> },
          { path: 'zones/list', element: <CachedZoneList /> },
          { path: 'packages/list', element: <CachedPackageList /> },
          { path: '*', element: <Navigate to="/cache/companies/list" /> },
        ]
      },
      { path: "/lookup",
        children: [
          { path: 'customer', element: <Customer /> },
          { path: 'customer/list', element: <CustomerList /> },
          { path: 'customer/:id', element: <Customer /> },
          { path: '*', element: <Navigate to="/lookup/customer" /> },
        ]
      },
      { path: "/partner/user",
        children: [
          { path: '', element: <Navigate to="/partner/user/list" /> },
          { path: 'list', element: <PartnerUserList /> },
          { path: 'create', element: <PartnerUserCreate /> },
          { path: 'edit/:username', element: <PartnerUserUpdate /> },
          { path: '*', element: <Navigate to="/partner/user/list" /> },
        ]
      },
      { path: "/distributor",
        children: [
          { path: '', element: <Navigate to="/distributor/list" /> },
          { path: 'add', element: <Distributor /> },
          { path: 'edit/:id', element: <Distributor /> },
          { path: 'list', element: <DistributorList /> },
          { path: 'details/:id', element: <DistributorDetails /> },
          { path: '*', element: <Navigate to="/distributor/list" /> },
        ]
      },
      { path: "/serverGroup",
        children: [
          { path: '', element: <Navigate to="/serverGroup/list" /> },
          { path: 'add', element: <ServerGroup /> },
          { path: 'edit/:id', element: <ServerGroup /> },
          { path: 'list', element: <ServerGroupList /> },
          { path: 'details/:id', element: <ServerGroupDetails /> },
          { path: '*', element: <Navigate to="/serverGroup/list" /> },
        ]
      },
      { path: "/zoneGroup",
        children: [
          { path: '', element: <Navigate to="/zoneGroup/list" /> },
          { path: 'add', element: <ZoneGroup /> },
          { path: 'edit/:id', element: <ZoneGroup /> },
          { path: 'list', element: <ZoneGroupList /> },
          { path: 'details/:id', element: <ZoneGroupDetails /> },
          { path: '*', element: <Navigate to="/zoneGroup/list" /> },
        ]
      },
      { path: "/zone",
        children: [
          { path: '', element: <Navigate to="/zone/list" /> },
          { path: 'add', element: <Zone /> },
          { path: 'statement', element: <ZoneStatement /> },
          { path: 'commissionRule', element: <CommissionRuleList /> },
          { path: 'commissionRule/:code', element: <CommissionRuleDetails /> },
          { path: 'commissionSlab/:code', element: <CommissionSlabDetails /> },
          { path: 'renew', element: <ZoneRenew /> },
          { path: 'commission', element: <ZoneCommission /> },
          { path: 'balance', element: <ZoneBalance /> },
          { path: 'allBalance', element: <AllZoneBalance /> },
          { path: 'edit/:id', element: <Zone /> },
          { path: 'list', element: <ZoneList /> },
          { path: 'details/:id', element: <ZoneDetails /> },
          { path: 'adjustment', element: <ZoneAdjustment /> },
          { path: 'transactions', element: <ZoneTransactions /> },
          { path: 'wallet/recharge', element: <ZoneWalletRecharge /> },
          { path: '*', element: <Navigate to="/zone/list" /> },
        ]
      },
      { path: "/legalOffice",
        children: [
          { path: '', element: <Navigate to="/legalOffice/list" /> },
          { path: 'add', element: <LegalOffice /> },
          { path: 'edit/:id', element: <LegalOffice /> },
          { path: 'list', element: <LegalOfficeList /> },
          { path: 'details/:id', element: <LegalOfficeDetails /> },
          { path: '*', element: <Navigate to="/legalOffice/list" /> },
        ]
      },
      { path: "/supportOffice",
        children: [
          { path: '', element: <Navigate to="/supportOffice/list" /> },
          { path: 'add', element: <SupportOffice /> },
          { path: 'edit/:id', element: <SupportOffice /> },
          { path: 'list', element: <SupportOfficeList /> },
          { path: 'details/:id', element: <SupportOfficeDetails /> },
          { path: '*', element: <Navigate to="/supportOffice/list" /> },
        ]
      },
      { path: "/server",
        children: [
          { path: '', element: <Navigate to="/server/list" /> },
          { path: 'add', element: <Server /> },
          { path: 'edit/:id', element: <Server /> },
          { path: 'list', element: <ServerList /> },
          { path: 'details/:id', element: <ServerDetails /> },
          { path: '*', element: <Navigate to="/server/list" /> },
        ]
      },
      { path: "/packageType",
        children: [
          { path: '', element: <Navigate to="/zoneGroup/list" /> },
          { path: 'add', element: <PackageType /> },
          { path: 'edit/:id', element: <PackageType /> },
          { path: 'list', element: <PackageTypeList /> },
          { path: 'details/:id', element: <PackageTypeDetails /> },
          { path: '*', element: <Navigate to="/packageType/list" /> },
        ]
      },
      { path: "/packages",
        children: [
          { path: '', element: <Navigate to="/zoneGroup/list" /> },
          { path: 'edit/:id', element: <PackageDetails /> },
          { path: 'list', element: <PackageList /> },
          { path: '*', element: <Navigate to="/packages/list" /> },
        ]
      },
      { path: "/iplog",
        children: [
          { path: 'search', element: <IpLogSearch /> },
          { path: '*', element: <Navigate to="/iplog/search" /> },
        ]
      },
      { path: "/dashboard",
        children: [
          { path: 'analytical', element: <Analytical /> },
          { path: '*', element: <Navigate to="/dashboard/analytical" /> },
        ]
      },
      { path: "/bulk-upload",
        children: [
          { path: '', element: <UploadList /> },
          { path: '*', element: <Navigate to="/bulk-upload" /> },
        ]
      },
      { path: "/report/sync-invoice-count",
        children: [
          { path: '', element: < InvoiceSyncCountReport /> },
          { path: '*', element: <Navigate to="report/sync-invoice-count" /> },
        ]
      },
      { path: "/report/orphan-invoices",
        children: [
          { path: '', element: < OrphanInvoices /> },
          { path: '*', element: <Navigate to="report/orphan-invoices" /> },
        ]
      },
      { path: "/notifications",
        children: [
          { path: '', element: <Navigate to="/notifications/dashboard" /> },
          { path: 'dashboard', element: <NotificationDashboard /> },
          { path: 'templates', element: <NotificationTemplates /> },
          { path: 'routes', element: <NotificationRoutes /> },
          { path: 'email-config', element: <NotificationEmailConfig /> },
          { path: 'sms-config', element: <NotificationSmsConfig /> },
          { path: 'send', element: <NotificationSend /> },
          { path: '*', element: <Navigate to="/notifications/dashboard" /> },
        ]
      }
    ],
  },
  { path: "/callback", element: <OAuthPopup /> },
  {
    path: "/auth",
    element: <BlankLayout />,
    children: [
      { path: "404", element: <Error /> },
      { path: "*", element: <Navigate to="/auth/404" /> },
    ],
  },
  { path: "/iplog",
    element: <BlankLayout />,
    children: [
      { path: 'pdf', element: <IpLogPdf /> },
    ]
  },
  {
    path: "/login",
    element: <Login />
  },
  {
    path: "/unauthorized",
    element: <Unauthorized />
  },
  {
    path: "/error",
    element: <Error />
  }
];

export default ThemeRoutes;
